import React, { useEffect, useState } from 'react';
import api from '../../services/api';

/* The school's own settings — identity, voices, and how the AI interviewer
 * carries itself. Nobody writes prompts here: six sliders with plain-language
 * ends, and a feedback box for "what didn't match". The server turns both
 * into the interviewer's instructions.
 */

/* One feedback note with its Prompt Tuner trail: the school's words, the
   validated instruction the judges follow, and the critic's evidence. */
const FeedbackNote = ({ f }) => (
  <div className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 space-y-1">
    <div>
      {f.mode === 'refined' && f.refined ? f.refined : f.text}
      <span className="text-slate-400"> — {new Date(f.at).toLocaleDateString()}</span>
    </div>
    {f.mode === 'refined' && f.refined ? (
      <details>
        <summary className="cursor-pointer text-emerald-700">✓ Validated with evidence — see how</summary>
        <div className="mt-1.5 space-y-1 pl-2 border-l-2 border-emerald-200">
          <div><span className="text-slate-400">You wrote:</span> “{f.text}”</div>
          {(f.critique?.evidence || []).map((e, i) => (
            <div key={i}>
              <span className="text-slate-400">Instruction says</span> “{e.clause}” <span className="text-slate-400">because you said</span> “{e.quote}”
            </div>
          ))}
          <div className="text-slate-400">Checked in {f.critique?.iterations || 1} round{(f.critique?.iterations || 1) > 1 ? 's' : ''}: faithful to your words, nothing added, applies to every candidate equally.</div>
        </div>
      </details>
    ) : f.critique ? (
      <details>
        <summary className="cursor-pointer text-amber-700">Used exactly as written — the refinement check did not pass. See why</summary>
        <div className="mt-1.5 space-y-1 pl-2 border-l-2 border-amber-200">
          <div className="text-slate-500">Your words are being followed as given — nothing is lost. The checking agent could not produce a rewording it could fully justify:</div>
          {(f.critique?.objections || []).map((o, i) => (
            <div key={i} className="text-slate-600">• {o}</div>
          ))}
          <div className="text-slate-400">Tried {f.critique?.iterations || 1} round{(f.critique?.iterations || 1) > 1 ? 's' : ''}. Saving the note again will re-run the check.</div>
        </div>
      </details>
    ) : null}
  </div>
);

/* A smooth slider over five real stops. The drag is continuous so it glides
   under the finger; on release it settles onto the nearest stop, because each
   stop maps to a specific instruction sentence — 3.4 means nothing. */
const SliderRow = ({ label, low, high, value, onChange }) => {
  const [live, setLive] = useState(value ?? 3);
  useEffect(() => { setLive(value ?? 3); }, [value]);
  const commit = () => {
    const v = Math.round(live);
    setLive(v);
    onChange(v);
  };
  const pct = ((live - 1) / 4) * 100;
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-xs text-slate-400">{Math.round(live)} / 5</span>
      </div>
      <input
        type="range"
        min="1"
        max="5"
        step="0.01"
        value={live}
        onChange={(e) => setLive(Number(e.target.value))}
        onMouseUp={commit}
        onTouchEnd={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="dps-range w-full"
        style={{ backgroundSize: `${pct}% 100%` }}
      />
      <div className="flex justify-between text-xs text-slate-400 mt-0.5">
        <span>{low}</span>
        <span>{high}</span>
      </div>
    </div>
  );
};

const RANGE_CSS = `
.dps-range { -webkit-appearance: none; appearance: none; height: 6px; border-radius: 9999px;
  background-color: #e2e8f0; background-image: linear-gradient(#0B7A82, #0B7A82);
  background-repeat: no-repeat; outline: none; cursor: pointer; }
.dps-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none;
  width: 20px; height: 20px; border-radius: 50%; background: #fff;
  border: 2px solid #0B7A82; box-shadow: 0 1px 4px rgba(0,0,0,.18);
  transition: transform .12s ease, box-shadow .12s ease; }
.dps-range::-webkit-slider-thumb:hover { transform: scale(1.18); box-shadow: 0 2px 8px rgba(234,61,15,.35); }
.dps-range::-webkit-slider-thumb:active { transform: scale(1.25); }
.dps-range::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #fff;
  border: 2px solid #0B7A82; box-shadow: 0 1px 4px rgba(0,0,0,.18); cursor: pointer; }
.dps-range::-moz-range-track { height: 6px; border-radius: 9999px; background: transparent; }
`;

const SLIDERS = [
  { key: 'warmth',        label: 'Warmth',        low: 'Reserved',        high: 'Warm & personable' },
  { key: 'formality',     label: 'Formality',     low: 'Conversational',  high: 'Formal' },
  { key: 'probing',       label: 'Follow-ups',    low: 'Gentle',          high: 'Presses hard' },
  { key: 'pace',          label: 'Pace',          low: 'Unhurried',       high: 'Brisk' },
  { key: 'languageLevel', label: 'Language',      low: 'Simple words',    high: 'Sophisticated' },
  { key: 'encouragement', label: 'Encouragement', low: 'Neutral',         high: 'Very encouraging' },
];

/* Human-readable diff between the loaded settings and the edited ones, shown
   on the confirm step so nobody saves a change they didn't mean to make. */
function describeChanges(orig, s, feedbackText, evalFeedbackText) {
  const out = [];
  if (!orig) return out;
  if (orig.schoolName !== s.schoolName) out.push(`School name: “${orig.schoolName}” → “${s.schoolName}”`);
  if (orig.shortName !== s.shortName) out.push(`Short name: “${orig.shortName}” → “${s.shortName}”`);
  if ((orig.examGuideUrl || '') !== (s.examGuideUrl || '')) out.push(`Exam guide video: ${s.examGuideUrl ? `“${s.examGuideUrl}”` : 'removed'}`);
  for (const lang of ['en', 'hi']) {
    if ((orig.voices?.[lang] || '') !== (s.voices?.[lang] || '')) {
      out.push(`${lang === 'en' ? 'English' : 'Hindi'} voice: ${orig.voices?.[lang] || 'default'} → ${s.voices?.[lang] || 'default'}`);
    }
  }
  for (const { key, label } of SLIDERS) {
    const a = orig.style?.[key] ?? 3;
    const b = s.style?.[key] ?? 3;
    if (a !== b) out.push(`${label}: ${a} → ${b}`);
  }
  const a = orig.evaluation?.strictness ?? 3;
  const b = s.evaluation?.strictness ?? 3;
  if (a !== b) out.push(`Marking strictness: ${a} → ${b}`);
  if (feedbackText.trim()) out.push(`New interviewer note: “${feedbackText.trim()}”`);
  if (evalFeedbackText.trim()) out.push(`New marking note: “${evalFeedbackText.trim()}”`);
  return out;
}

const SchoolInterviewerSettings = () => {
  const [s, setS] = useState(null);
  const [orig, setOrig] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [evalFeedbackText, setEvalFeedbackText] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    api.get('/school-settings')
      .then((r) => {
        setS(r.data.data);
        setOrig(JSON.parse(JSON.stringify(r.data.data)));
      })
      .catch(() => setNote('Could not load settings.'));
  }, []);

  const save = async () => {
    setSaving(true);
    setNote('');
    try {
      const r = await api.put('/school-settings', {
        schoolName: s.schoolName,
        shortName: s.shortName,
        examGuideUrl: s.examGuideUrl || '',
        voices: s.voices,
        style: s.style,
        evaluation: s.evaluation,
        feedbackText,
        evaluationFeedbackText: evalFeedbackText,
      });
      setS(r.data.data);
      setOrig(JSON.parse(JSON.stringify(r.data.data)));
      setFeedbackText('');
      setEvalFeedbackText('');
      setConfirming(false);
      setNote('Saved. The very next interview and screening will use these settings.');
    } catch {
      setNote('Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-sm text-slate-500">{note || 'Loading…'}</div>;

  const setStyle = (key, v) => setS({ ...s, style: { ...s.style, [key]: Number(v) } });
  const changes = describeChanges(orig, s, feedbackText, evalFeedbackText);

  /* ── The gate: what this does, before any dial is touchable ─────────── */
  if (!gateOpen) {
    return (
      <div className="max-w-xl space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">School & AI interviewer settings</h2>
          <p className="text-sm text-slate-500 mt-1">Read this before you change anything here.</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3 text-sm text-slate-700">
          <p className="font-semibold text-amber-800">⚠ These settings change how AI treats every candidate.</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>What this screen controls:</b> how the AI interviewer speaks and carries the conversation, which voice it uses, and how strictly AI marks — CV screening, written exam answers, and interview reports.</li>
            <li><b>What a change does:</b> it applies to <b>every future interview and screening for this school</b>, starting from the very next one. Past results are never changed.</li>
            <li><b>What it does not do:</b> it never applies to one candidate — every dial applies equally to everyone, which is what keeps the assessment fair.</li>
            <li><b>When to proceed:</b> only when you have specific, agreed feedback from actual interviews or assessments — “too sharp with nervous candidates”, “CV scores too harsh on freshers”. Curiosity is not a reason to move a dial.</li>
            <li><b>Your feedback is validated before it acts:</b> a checking agent (the Prompt Tuner) turns your words into a precise instruction and verifies it against what you wrote, clause by clause — you'll see the instruction and its evidence under each saved note.</li>
            <li>Every change is recorded in the activity log with your name.</li>
          </ul>
        </div>
        <button
          onClick={() => setGateOpen(true)}
          className="px-5 py-2.5 rounded-xl bg-meraki-indigo text-white text-sm font-medium"
        >
          I have specific feedback — open the settings
        </button>
      </div>
    );
  }

  /* ── The confirm step: what exactly is about to change ──────────────── */
  if (confirming) {
    return (
      <div className="max-w-xl space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Confirm these changes</h2>
          <p className="text-sm text-slate-500 mt-1">
            From the next interview and screening onwards, for every candidate of this school:
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          {changes.length ? (
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
              {changes.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No changes — nothing will be saved.</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving || !changes.length}
            className="px-5 py-2.5 rounded-xl bg-meraki-indigo text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Confirm and save'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium"
          >
            Keep editing
          </button>
          {note && <span className="text-sm text-slate-500">{note}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <style>{RANGE_CSS}</style>
      <div>
        <h2 className="text-lg font-semibold text-slate-800">School & AI interviewer</h2>
        <p className="text-sm text-slate-500 mt-1">
          How the AI interviewer introduces itself and carries the conversation, for this school only.
          No prompts to write — set the dials, and tell it what didn't match.
        </p>
      </div>

      {/* Identity */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">School name</label>
          <input
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
            value={s.schoolName}
            onChange={(e) => setS({ ...s, schoolName: e.target.value })}
          />
          <p className="text-xs text-slate-400 mt-1">Spoken by the interviewer: “…from {s.schoolName || 'the school'}”.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Short name</label>
          <input
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
            value={s.shortName}
            onChange={(e) => setS({ ...s, shortName: e.target.value })}
          />
        </div>
      </div>

      {/* Shikha, walkthrough 30 Oct 2026: a short video on how the online
          written test works, shown on the exam's start page. */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Exam guide video (optional)</label>
        <input
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
          placeholder="https://… a short video on how the online exam works"
          value={s.examGuideUrl || ''}
          onChange={(e) => setS({ ...s, examGuideUrl: e.target.value })}
        />
        <p className="text-xs text-slate-400 mt-1">Shown to candidates on the written test's start page, before they begin.</p>
      </div>

      {/* Voices */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">English voice ID</label>
          <input
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-mono"
            placeholder="Blank = system default"
            value={s.voices?.en || ''}
            onChange={(e) => setS({ ...s, voices: { ...s.voices, en: e.target.value } })}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Hindi voice ID</label>
          <input
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-mono"
            placeholder="Blank = system default"
            value={s.voices?.hi || ''}
            onChange={(e) => setS({ ...s, voices: { ...s.voices, hi: e.target.value } })}
          />
        </div>
        <p className="col-span-2 text-xs text-slate-400 -mt-2">
          A voice ID from the ElevenLabs account's “My Voices”. Leave blank to use the platform default.
        </p>
      </div>

      {/* Style sliders */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Interviewer style</h3>
        <div className="space-y-5">
          {SLIDERS.map(({ key, label, low, high }) => (
            <SliderRow
              key={key}
              label={label}
              low={low}
              high={high}
              value={s.style?.[key] ?? 3}
              onChange={(v) => setStyle(key, v)}
            />
          ))}
        </div>
      </div>

      {/* Feedback */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-1.5">What didn't match?</h3>
        <p className="text-xs text-slate-500 mb-2">
          Describe the gap in your own words — “too sharp with nervous candidates”, “didn't sound like our school”.
          The interviewer follows the most recent notes in every interview.
        </p>
        <textarea
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm min-h-[72px]"
          placeholder="e.g. Too sharp on follow-up questions — soften with freshers."
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
        />
        {(s.feedback || []).length > 0 && (
          <div className="mt-3 space-y-1.5">
            <div className="text-xs font-semibold text-slate-500 uppercase">Active notes (latest five apply)</div>
            {(s.feedback || []).slice(-5).reverse().map((f, i) => <FeedbackNote key={i} f={f} />)}
          </div>
        )}
      </div>

      {/* Evaluation — governs the AI judges: CV screening, subjective exam
          marking, interview-report scoring */}
      <div className="border-t border-slate-100 pt-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-1.5">Marking & evaluation</h3>
        <p className="text-xs text-slate-500 mb-4">
          Applies wherever AI marks — CV screening, written exam answers, and interview reports.
        </p>
        <SliderRow
          label="Marking strictness"
          low="Generous — benefit of the doubt"
          high="Strict — only what's demonstrated"
          value={s.evaluation?.strictness ?? 3}
          onChange={(v) => setS({ ...s, evaluation: { ...s.evaluation, strictness: v } })}
        />
        <div className="mt-4">
          <p className="text-xs text-slate-500 mb-2">
            What did the marking get wrong? — “CV scores too harsh on freshers”, “exam marking ignores partially right answers”.
          </p>
          <textarea
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm min-h-[60px]"
            placeholder="e.g. Give more weight to hands-on classroom experience than certificates."
            value={evalFeedbackText}
            onChange={(e) => setEvalFeedbackText(e.target.value)}
          />
          {(s.evaluationFeedback || []).length > 0 && (
            <div className="mt-3 space-y-1.5">
              <div className="text-xs font-semibold text-slate-500 uppercase">Active marking notes (latest five apply)</div>
              {(s.evaluationFeedback || []).slice(-5).reverse().map((f, i) => <FeedbackNote key={i} f={f} />)}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => { setNote(''); setConfirming(true); }}
          disabled={!changes.length}
          className="px-5 py-2.5 rounded-xl bg-meraki-indigo text-white text-sm font-medium disabled:opacity-50"
        >
          Review changes…
        </button>
        {!changes.length && <span className="text-sm text-slate-400">Nothing changed yet.</span>}
        {note && <span className="text-sm text-slate-500">{note}</span>}
      </div>
    </div>
  );
};

export default SchoolInterviewerSettings;
